type CategoryTree = Awaited<
	ReturnType<ReturnType<typeof useNorcePwa>["getCategoryTree"]>
>;

/**
 * Requests still in flight in the browser, keyed by locale and store
 * context. The desktop and mobile headers both mount on hydration and ask
 * for the tree before either response has reached the store; they share one
 * request. Not used on the server: this module is shared by every request
 * there, and two renders must never hand each other the same array.
 */
const inFlight = new Map<string, Promise<CategoryTree>>();

// Nuxt replaces import.meta.client at build time; unit tests fall back to
// whether a window exists.
const isBrowser = () => import.meta.client ?? typeof window !== "undefined";

const sharedRequest = (
	key: string,
	start: () => Promise<CategoryTree>
): Promise<CategoryTree> => {
	const pending = inFlight.get(key);
	if (pending) return pending;
	const request = start().finally(() => inFlight.delete(key));
	inFlight.set(key, request);
	return request;
};

export const useFetchCategoryTree = () => {
	const { getCategoryTree } = useNorcePwa();

	const fetchCategoryTree = async (locale: string) => {
		const cached = useCategoryStore().categoryTree[locale];
		if (cached?.length) return cached;

		const storeContext = useNorceStore().storeContext;
		const categories = await (isBrowser()
			? sharedRequest(`${locale}:${JSON.stringify(storeContext)}`, () =>
					getCategoryTree({ ...storeContext })
				)
			: getCategoryTree({ ...storeContext }));
		useCategoryStore().setCategoryTree({ [locale]: categories });
		return categories;
	};

	return { fetchCategoryTree };
};
